import { RootState } from '../..';
import { CategoryEntity } from '../../../model/CategoryEntity';
import { createSlice } from '@reduxjs/toolkit';
import {
    fetchCategories,
    CategoriesResponse,
    createCategory,
    deleteCategory,
    updateCategory,
} from './categoriesAPI';
import { IPagination, LoadStatus } from '../../../model';
import { createAppThunk } from '../../thunks';

export interface CategoriesPanelState {
    loading: LoadStatus;
    items: CategoryEntity[];
    search: string | null;
    nextPageToken: string | null;
    pagination: IPagination;
}

const initialState: CategoriesPanelState = {
    loading: 'loading',
    items: [],
    search: null,
    nextPageToken: null,
    pagination: {
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
    },
};

const sanitizeKey = (key: string) => key.replace(/[/\\#?]/g, '');

const getUniqueCategories = (categories: CategoryEntity[]) => {
    const categoriesMap = new Map(categories.map((x) => [x.rowKey, x]));
    const unique = Array.from(
        categoriesMap,
        ([rowKey, value]): CategoryEntity => ({
            rowKey,
            title: value.title,
        })
    );
    return unique.sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
    );
};

export const loadCategories = createAppThunk<CategoriesResponse>(
    'categoriesPanel/loadCategories',
    async (_, { getState }) => {
        const {
            categoriesPanel: { nextPageToken, search },
        } = getState();
        const queryParams = new Map();

        if (!!search) {
            queryParams.set('search', search);
        }

        if (!!nextPageToken) {
            queryParams.set('nextPageToken', nextPageToken);
        }

        const query: string[] = [];
        queryParams.forEach((val, key) => {
            if (query.length) {
                query.push(`&${key}=${val}`);
            } else {
                query.push(`?${key}=${val}`);
            }
        });

        return await fetchCategories(query.join(''));
    }
);

export const searchCategories = createAppThunk<CategoriesResponse, string>(
    'categoriesPanel/searchCategories',
    async (search) => {
        const query: string = `?search=${search}`;
        return await fetchCategories(query);
    }
);

export const createCategories = createAppThunk<CategoryEntity, CategoryEntity>(
    'categoriesPanel/createCategory',
    async (newCategory) => {
        const sanitizedRowKey = sanitizeKey(newCategory.title);
        const categoryToCreate = {
            ...newCategory,
            rowKey: sanitizedRowKey,
        };
        const createdCategory = await createCategory(categoryToCreate);
        return createdCategory;
    }
);

export const updateCategories = createAppThunk<CategoryEntity, CategoryEntity>(
    'categoriesPanel/updateCategory',
    async (newCategory) => {
        return await updateCategory(newCategory);
    }
);

export const deleteCategories = createAppThunk<string, string>(
    'categoriesPanel/deleteCategory',
    async (rowKey: string) => {
        await deleteCategory(rowKey);
        return rowKey;
    }
);

export const categoriesSlice = createSlice({
    name: 'categoriesPanel',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(loadCategories.pending, (state) => {
                state.loading = 'loading';
            })
            .addCase(loadCategories.fulfilled, (state, action) => {
                state.loading = 'idle';
                state.items = getUniqueCategories([
                    ...state.items,
                    ...(action.payload?.items ?? []),
                ]);
            })
            .addCase(loadCategories.rejected, (state) => {
                state.loading = 'failed';
            })
            .addCase(createCategories.pending, (state) => {
                state.loading = 'loading';
            })
            .addCase(createCategories.fulfilled, (state, action) => {
                state.loading = 'idle';
                if (
                    !state.search ||
                    action.payload.rowKey.includes(state.search.toUpperCase())
                ) {
                    state.items = getUniqueCategories([
                        ...state.items,
                        action.payload,
                    ]);
                }
            })
            .addCase(createCategories.rejected, (state) => {
                state.loading = 'failed';
            })
            .addCase(updateCategories.pending, (state) => {
                state.loading = 'loading';
            })
            .addCase(updateCategories.fulfilled, (state, action) => {
                state.loading = 'idle';
                state.items = getUniqueCategories(
                    state.items.map((x) =>
                        x.rowKey === action.payload.rowKey
                            ? { ...action.payload }
                            : x
                    )
                );
            })
            .addCase(updateCategories.rejected, (state) => {
                state.loading = 'failed';
            })
            .addCase(deleteCategories.pending, (state) => {
                state.loading = 'loading';
            })
            .addCase(deleteCategories.fulfilled, (state, action) => {
                state.loading = 'idle';
                state.items = state.items.filter(
                    (x) => x.rowKey !== action.payload
                );
            })
            .addCase(deleteCategories.rejected, (state) => {
                state.loading = 'failed';
            })

            .addCase(searchCategories.pending, (state) => {
                state.loading = 'loading';
            })
            .addCase(searchCategories.fulfilled, (state, action) => {
                state.loading = 'idle';
                state.items = action.payload?.items ?? [];
                state.search = action.payload.search;
                state.nextPageToken = !!action.payload.nextPageToken
                    ? JSON.stringify(action.payload.nextPageToken)
                    : null;
            })
            .addCase(searchCategories.rejected, (state) => {
                state.loading = 'failed';
            });
    },
});

export const selectCategories = (state: RootState) =>
    state.categoriesPanel.items;

export default categoriesSlice.reducer;
